import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ImageMapper from 'react-image-mapper';
import Floater from 'react-floater';
import { icons, images } from '../../../../assets';
import { formatValue, notifyError } from '../../../../utils';
import {
  putAddImagePin,
  getImagePins,
  deleteImagePin,
  patchImagePin,
} from '../../../../services';
import * as s from './styled-thing';

const defaultImageSize = { x: 816, y: 570 };

const Thing = ({
  values,
  placePin,
  setPlacePin,
  thingId,
  pins,
  setPins,
  highlightVar,
  setHighlightVar,
  image,
}) => {
  const { user } = useSelector((state) => state.usuario);
  const [mouseCoords, setMouseCoords] = useState({
    x: null,
    y: null,
  });
  const [loading, setLoading] = useState(false);
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
  });
  // Drag de reposicionamento de pin: dragId = id do pin sendo arrastado,
  // dragPos = posicao atual do cursor em px relativos ao ImageMapperWrapper.
  const [dragId, setDragId] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  // Proporcao NATURAL da imagem carregada (varia por aplicacao). Antes
  // assumiamos sempre 816x570 (defaultImageSize), o que deixava sobra de
  // espaco e desalinhava os pins quando a imagem tinha outra proporcao.
  const [imgNatural, setImgNatural] = useState(null);
  // Ref do wrapper da imagem (base de coordenadas dos pins) — usado no
  // drag pra converter clientX/Y do mouse em px sobre a imagem.
  const imgWrapperRef = useRef(null);
  const canEditPins = user.role !== 'visualizador';

  // Geometria de exibicao: largura = largura disponivel (medida); altura
  // proporcional a proporcao NATURAL da imagem (fallback 816x570 ate
  // carregar). Assim a imagem preenche a largura sem distorcer e o quadro
  // acompanha a altura real (sem sobra). Os pins usam dispW/dispH.
  const dispW = imageSize.width;
  const dispH =
    imgNatural && imgNatural.w
      ? Math.round((dispW * imgNatural.h) / imgNatural.w)
      : Math.round((dispW * defaultImageSize.y) / defaultImageSize.x);

  // Conversores pin <-> px. cordx/cordy ficam normalizados no espaco
  // defaultImageSize (816x570) — fracao da imagem. px = fracao * dispW/H.
  const cordToPx = (coords) => ({
    x: Math.round((coords.x * dispW) / defaultImageSize.x),
    y: Math.round((coords.y * dispH) / defaultImageSize.y),
  });
  const pxToCord = (px, py) => ({
    x: dispW ? Math.round((px * defaultImageSize.x) / dispW) : 0,
    y: dispH ? Math.round((py * defaultImageSize.y) / dispH) : 0,
  });

  // Carrega a imagem fora da arvore so pra ler naturalWidth/Height e
  // descobrir a proporcao real. Refaz quando a src (image) muda.
  useEffect(() => {
    let active = true;
    const src = image ? image.url : images.BombaFull;
    if (!src) return undefined;
    const im = new Image();
    im.onload = () => {
      if (active && im.naturalWidth && im.naturalHeight) {
        setImgNatural({ w: im.naturalWidth, h: im.naturalHeight });
      }
    };
    im.src = src;
    return () => {
      active = false;
    };
  }, [image]);

  // Antes: setTimeout(150) lia clientWidth/Height e setava imageSize.
  // Problema: se o layout demorava mais que 150ms pra resolver (page-level
  // scroll deixa altura indefinida e min-height vh-relativa demora pra
  // estabilizar), as dimensoes vinham 0 e a ImageMapper nao renderizava.
  //
  // Agora: ResizeObserver dispara sempre que o wrapper muda de tamanho —
  // captura mount, layout settling, resize de janela e mudancas de
  // viewport, tudo em um listener so. Pega clientRect.width/height ao
  // vivo, entao imageSize sempre reflete o tamanho real do wrapper.
  const wrapperRef = useRef(null);

  useEffect(() => {
    buscaPins();
    const el = document.getElementById('image-line-wrapper');
    if (!el) return undefined;
    wrapperRef.current = el;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setImageSize({
          width: Math.round(width) || 0,
          height: Math.round(height) || 0,
        });
      }
    });
    ro.observe(el);
    // Snapshot inicial caso ResizeObserver demore pra disparar:
    setImageSize({
      width: el.clientWidth || 0,
      height: el.clientHeight || 0,
    });
    return () => ro.disconnect();
    // Mount-only: registra listener uma única vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscaPins = () => {
    setLoading(true);
    getImagePins(parseInt(thingId, 10))
      .then(({ data }) => {
        setPins(data);
      })
      .catch(() => {
        // Erro 404 e esperado (thing sem pins). Outros erros silenciamos
        // mas mantemos a tela utilizavel — pins sao opcionais.
        notifyError('Não foi possível carregar os pins desta máquina');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAddPin = (newCoords, variable) => {
    // Converte o clique (px sobre a imagem exibida) pra coordenada
    // normalizada usando a geometria real de exibicao (dispW/dispH).
    const cord = pxToCord(newCoords.x, newCoords.y);
    putAddImagePin({
      thing: parseInt(thingId, 10),
      variable: parseInt(variable.id, 10),
      cordx: cord.x,
      cordy: cord.y,
      position: 'right',
    })
      .then(() => {
        buscaPins();
        setMouseCoords({ x: null, y: null });
        setPlacePin(false);
      })
      .catch(() => {
        notifyError('Não foi possível adicionar o pin');
        setPlacePin(false);
      });
  };

  const handleDeletePin = (pinId) => {
    setLoading(true);
    deleteImagePin(parseInt(pinId, 10))
      .then(() => {
        const newPins = pins.filter(({ id }) => id !== pinId);
        setPins(newPins);
      })
      .catch(() => {
        notifyError('Não foi possível remover o pin');
      })
      .finally(() => setLoading(false));
  };

  const handlePosition = (coords) => cordToPx(coords);

  const handleTurnPin = (id, coords, position) => {
    setLoading(true);
    let newPosition = 'right';
    if (position === 'right') newPosition = 'bottom';
    if (position === 'bottom') newPosition = 'left';
    if (position === 'left') newPosition = 'top';

    const { x, y } = coords;

    patchImagePin(parseInt(id, 10), {
      cordx: x,
      cordy: y,
      position: newPosition,
    })
      .then(() => {
        buscaPins();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // Persiste a nova posicao do pin (apos soltar o drag). cordx/cordy ja
  // vem em coordenadas-fonte (816x570), convertidas no mouseup.
  const handleRepositionPin = (id, cordx, cordy, position) => {
    patchImagePin(parseInt(id, 10), { cordx, cordy, position })
      .then(() => buscaPins())
      .catch(() => notifyError('Não foi possível reposicionar o pin'));
  };

  // Drag: enquanto dragId esta setado, escuta mousemove/mouseup no
  // document (o cursor pode sair do wrapper durante o arrasto). No move,
  // atualiza dragPos (px relativos ao wrapper) pra o dot seguir o cursor;
  // no up, converte pra coordenada-fonte (divide pela escala por largura)
  // e persiste. Cleanup remove os listeners — sem isso vazaria handler a
  // cada drag.
  useEffect(() => {
    if (dragId === null) return undefined;

    const toSourceCoords = (clientX, clientY) => {
      const rect = imgWrapperRef.current
        ? imgWrapperRef.current.getBoundingClientRect()
        : null;
      if (!rect || !dispW || !dispH) return null;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      return { px: { x, y }, src: pxToCord(x, y) };
    };

    const onMove = (e) => {
      const c = toSourceCoords(e.clientX, e.clientY);
      if (c) setDragPos(c.px);
    };
    const onUp = (e) => {
      const c = toSourceCoords(e.clientX, e.clientY);
      const pin = pins.find((p) => p.id === dragId);
      if (c && pin) {
        handleRepositionPin(dragId, c.src.x, c.src.y, pin.position || 'right');
      }
      setDragId(null);
      setDragPos(null);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    // handleRepositionPin/buscaPins sao estaveis no escopo do render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragId, dispW, dispH, pins]);

  const renderFloaterContent = (
    { name, ihmname, id, unit },
    coords,
    pinId,
    position
  ) => {
    return (
      <s.FloaterWrapper
        onMouseEnter={() => highlightVar && setHighlightVar(false)}
        highlight={highlightVar === id}
      >
        <label>{name}</label>
        <button
          style={{ gridArea: 'c' }}
          type="button"
          aria-label="Girar pin"
          onClick={() => handleTurnPin(pinId, coords, position)}
        >
          <img src={icons.Turn} alt="" />
        </button>
        <h4>
          {formatValue(values[ihmname])} {unit}
        </h4>
        {user.role !== 'visualizador' && (
          <button
            style={{ gridArea: 'd' }}
            type="button"
            aria-label="Remover pin"
            title="Remover pin"
            onClick={() => {
              // Confirmacao para acao destrutiva — antes deletava na hora,
              // sem chance de cancelar (operador acertava o pin por acidente
              // e perdia o ponto de monitoramento configurado).
              // eslint-disable-next-line no-alert
              if (window.confirm(`Remover o pin "${name}"?`)) {
                handleDeletePin(pinId);
              }
            }}
          >
            <img src={icons.TrashIcon} alt="" />
          </button>
        )}
      </s.FloaterWrapper>
    );
  };

  return (
    <>
      <s.Container>
        {/* Titulo "MÁQUINA <nome>" removido — redundante com o header da
            pagina e o breadcrumb, que ja exibem o nome da maquina. */}
        <s.Line
          style={{ flex: 1 }}
          center
          id="image-line-wrapper"
          minWidth={'100%'}
          minHeight={'0'}
          maxWidth={'100%'}
          maxHeight={'none'}
          maxHeightMedia={'none'}
        >
          <s.ImageMapperWrapper
            ref={imgWrapperRef}
            overlayed={placePin}
            imageWidth={imageSize.width}
            imageHeight={imageSize.height}
            onMouseLeave={() => setMouseCoords({ x: null, y: null })}
            onClick={() => {
              if (placePin) handleAddPin(mouseCoords, placePin);
            }}
            pointer={placePin}
          >
            <>
              {imageSize.width && dispH && (
                <>
                  <ImageMapper
                    src={image ? image.url : images.BombaFull}
                    width={imageSize.width}
                    height={dispH}
                    onImageMouseMove={({ nativeEvent }) => {
                      if (placePin)
                        setMouseCoords({
                          x: nativeEvent.layerX,
                          y: nativeEvent.layerY,
                        });
                    }}
                    map={{
                      name: 'imagem-mapeada',
                      areas: placePin
                        ? [
                            {
                              name: 'vertical-line',
                              shape: 'rect',
                              coords: [
                                mouseCoords.x,
                                dispH,
                                mouseCoords.x,
                                0,
                              ],
                              preFillColor: 'lightgrey',
                              fillColor: 'lightgrey',
                            },
                            {
                              name: 'horizontal-line',
                              shape: 'rect',
                              coords: [
                                imageSize.width,
                                mouseCoords.y,
                                0,
                                mouseCoords.y,
                              ],
                              preFillColor: 'lightgrey',
                              fillColor: 'lightgrey',
                            },
                          ]
                        : [],
                    }}
                  />
                </>
              )}

              {pins.map(({ cordx, cordy, variable, position, id }) => {
                const isDragging = dragId === id;
                // Enquanto arrasta, o dot segue o cursor (dragPos);
                // senao usa a posicao calculada a partir da coordenada-fonte.
                const dotCoords =
                  isDragging && dragPos
                    ? dragPos
                    : handlePosition({ x: cordx, y: cordy });
                // onMouseDown inicia o drag (so fora do modo "adicionar"
                // e pra quem pode editar). preventDefault evita o
                // ghost-drag nativo da imagem/elemento.
                const startDrag = canEditPins
                  ? (e) => {
                      if (placePin) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDragPos(dotCoords);
                      setDragId(id);
                    }
                  : undefined;

                const dot = (
                  <s.Pin
                    coords={dotCoords}
                    $draggable={canEditPins && !placePin}
                    $dragging={isDragging}
                    onMouseDown={startDrag}
                  />
                );

                // Durante o arrasto (ou loading) mostra so o dot, sem o
                // Floater — o balao reaparece com a nova posicao no drop.
                if (loading || isDragging) {
                  return <React.Fragment key={id}>{dot}</React.Fragment>;
                }

                return (
                  <Floater
                    key={id}
                    open={!loading}
                    placement={position}
                    styles={{
                      wrapper: {
                        zIndex: 1,
                      },
                      floater: {
                        zIndex: 1,
                      },
                      container: {
                        zIndex: placePin ? 3 : 1,
                        padding: 0,
                        borderRadius: 10,
                        minHeight: 0,
                        minWidth: 0,
                      },
                    }}
                    content={renderFloaterContent(
                      variable,
                      {
                        x: cordx,
                        y: cordy,
                      },
                      id,
                      position
                    )}
                    hideArrow
                    offset={5}
                  >
                    {dot}
                  </Floater>
                );
              })}
            </>
          </s.ImageMapperWrapper>
        </s.Line>
      </s.Container>
    </>
  );
};

export default Thing;
